import { PlusOutlined } from "@ant-design/icons"
import { Button, Flex, Modal, Skeleton, Typography } from "antd"
import React, { Suspense, lazy, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate } from "react-router-dom"

import {
  PermissionGate,
  type PermissionPath
} from "../../components/PermissionGate/PermissionGate.tsx"
import {
  type SubcontractorRateFormData,
  SubcontractorRateModal
} from "../../components/Subcontractor/SubcontractorRateModal.tsx"
import { isDeletedAccountRow } from "../../constants/deleted-account-ui.ts"
import { RoutesPaths } from "../../constants/routes.ts"
import { generateDocumentTitle } from "../../constants/document-title.ts"
import { SCROLL_PAGE_SIZE, nextPageUrl } from "../../redux/shared/paged-list.ts"
import type { RootState } from "../../store"
import { extractParamsFromNextUrl } from "../../utils/form/extract-params-from-next-url.ts"
import { serializeData } from "../../utils/utils"
import { SubcontractorFormModal } from "./components/SubcontractorForm/SubcontractorFormModal.tsx"
import { RESULT_TYPES } from "./constants/constants.ts"
import {
  subcontractorsClearFilters,
  subcontractorsSetFilters,
  subcontractorsSetProp
} from "./redux/subcontractors.slice.ts"
import {
  activateSubcontractor,
  createInviteSubcontractor,
  createSubcontractor,
  fetchSubcontractorDetails,
  fetchSubcontractors,
  fetchSubcontractorsFilterOptions,
  inviteNoAccessSubcontractor,
  rateSubcontractor,
  resendInviteSubcontractor,
  archiveSubcontractor,
  restoreSubcontractor,
  suspendSubcontractor,
  updateSubcontractor
} from "./redux/subcontractors.thunks.ts"
import { type SubcontractorsFilters } from "./redux/subcontractors.types.ts"
import { type SubcontractorListItem } from "./types/types.ts"
import { clearedSubcontractorFilters } from "./helpers/list-values.ts"

const SubcontractorsListMobile = lazy(
  () =>
    import("./components/SubcontractorsListMobile/SubcontractorsListMobile.tsx")
)

const SubcontractorsTable = lazy(
  () => import("./components/SubcontractorsList/SubcontractorsList.tsx")
)

const { Title, Text } = Typography

const CREATE_PERMISSIONS: PermissionPath[] = [
  "subcontractors.features.subcontractor_invite",
  "subcontractors.subcontractors.subcontractor_create"
]

const SubcontractorsPage = ({
  data,
  fetching,
  saving,
  isMobile,
  filters,
  options,
  saveResult,
  permissions,
  subcontractorDetails,
  subcontractorDetailsFetching,
  getSubcontractors,
  clearFilters,
  setFilters,
  createSubcontractor,
  rateSubcontractor,
  updateSubcontractor,
  setProp,
  getFilterOptions,
  archiveSubcontractor,
  restoreSubcontractor,
  suspendSubcontractor,
  createInviteSubcontractor,
  getSubcontractorDetails,
  activateSubcontractor,
  inviteNoAccessSubcontractor,
  resendInviteSubcontractor
}: ConnectedProps<typeof connector>) => {
  const { t } = useTranslation()
  const [ordering, setOrdering] = useState<string | null>(null)
  const [showSubcontractorForm, setShowSubcontractorForm] = useState(false)
  const [rateId, setRateId] = useState<string | null>(null)

  const subcontractors = data?.results || []
  const count = data?.count || 0
  const page = filters?.page || 1

  const navigate = useNavigate()
  const viewAllowed = permissions?.subcontractors?.subcontractor_view
  const editAllowed = permissions?.subcontractors?.subcontractor_edit
  const deleteAllowed = permissions?.subcontractors?.subcontractor_delete
  const inviteAllowed = permissions?.features?.subcontractor_invite

  const clearRateId = () => setRateId(null)
  const clearSubcontractorDetails = () => setProp("subcontractorDetails", null)

  // Stale details survive in Redux and would reopen the form in edit mode.
  const openCreate = () => {
    clearSubcontractorDetails()
    setShowSubcontractorForm(true)
  }

  const handleGetSubcontractors = useCallback(() => {
    const serializeFilters = serializeData(filters || {})

    getSubcontractors({
      ordering,
      ...serializeFilters,
      // The mobile card list still pages and owns `page`; the desktop table
      // scrolls, and a restored page would open it on rows it cannot scroll
      // back past.
      ...(isMobile ? {} : { page: 1 })
    } as SubcontractorsFilters)
  }, [filters, ordering, isMobile, getSubcontractors])

  const handleGetMoreSubcontractors = useCallback(() => {
    const url = nextPageUrl(data)
    if (!url) return
    // The server's own url carries the page and every filter it was built
    // with, so nothing here has to reconstruct them.
    getSubcontractors({
      ...extractParamsFromNextUrl<SubcontractorsFilters>(url),
      isMore: true
    } as SubcontractorsFilters & { isMore: boolean })
  }, [data?.next, data?.retryNext, getSubcontractors])

  const handleRateSubcontractor = (values: SubcontractorRateFormData) => {
    rateSubcontractor({
      id: rateId,
      text: values?.comment || "",
      score: values?.rating || 0
    })
  }

  const handleChangeFilter = useCallback(
    (newData: Partial<SubcontractorsFilters>) => {
      setFilters({ ...newData, page: 1 })
    },
    [setFilters]
  )

  const handleChangePage = useCallback(
    (page: number) => {
      setFilters({ page })
    },
    [setFilters]
  )

  // @todo fix type
  const handleCreateSubcontractor = ({ send_invitation, ...data }: any) => {
    if (subcontractorDetails) {
      updateSubcontractor({ ...data, id: subcontractorDetails.id })
      return
    }

    if (send_invitation) createInviteSubcontractor(data)
    else createSubcontractor(data)
  }

  const handleSubcontractorFormCancel = () => {
    if (subcontractorDetails) clearSubcontractorDetails()
    setShowSubcontractorForm(false)
  }

  const handleSelectRow = (record: SubcontractorListItem, actions: string) => {
    if (isDeletedAccountRow(record)) return

    const id = record?.id

    switch (actions) {
      // The management page, not the edit form: same destination as the row's
      // name link, in this tab, so Back returns to the list as it stands.
      case "manage":
        return navigate(RoutesPaths.subcontractors.view(id))
      case "edit":
      case "view_details":
        setShowSubcontractorForm(true)
        return getSubcontractorDetails(id)
      case "update":
        return updateSubcontractor(record)
      case "rate":
        return setRateId(id)
      case "archive":
        return Modal.confirm({
          centered: true,
          title: "Archive this subcontractor?",
          content:
            "They leave the directory but nothing is deleted — their projects " +
            "and documents stay, and you can restore them from the Archived tab.",
          cancelText: "Cancel",
          okText: "Archive",
          okButtonProps: { type: "primary" },
          onOk() {
            archiveSubcontractor(id)
          }
        })
      case "restore":
        // No confirmation: restoring puts a record back where it was and is
        // itself undone by archiving again. A dialog for a reversible,
        // non-destructive act is noise.
        return restoreSubcontractor(id)
      case "suspend": {
        return Modal.confirm({
          centered: true,
          title: "Confirm subcontractor blocking",
          content: "Are you sure you want to block this subcontractor?",
          cancelText: "Cancel",
          okText: "Confirm",
          okButtonProps: { type: "primary" },
          onOk() {
            suspendSubcontractor(id)
          }
        })
      }
      case "activate":
        return Modal.confirm({
          centered: true,
          title: "Confirm subcontractor activation",
          content: "Are you sure you want to activate this subcontractor?",
          cancelText: "Cancel",
          okText: "Confirm",
          okButtonProps: { type: "primary" },
          onOk() {
            activateSubcontractor(id)
          }
        })
      case "invite":
      case "resend_invite":
        if (record?.status?.toLowerCase() === "no access") {
          return inviteNoAccessSubcontractor(id)
        }

        return resendInviteSubcontractor(id)
    }
  }

  // @todo remove it and use promise result
  useEffect(() => {
    if (!saveResult) {
      handleGetSubcontractors()
    } else {
      if (
        [
          RESULT_TYPES.UPDATE,
          RESULT_TYPES.INVITE,
          RESULT_TYPES.CREATE
        ]?.includes(saveResult?.type)
      ) {
        setProp("saveResult", null)
        setShowSubcontractorForm(false)
        if (subcontractorDetails) clearSubcontractorDetails()
      }

      if (
        [
          RESULT_TYPES.UNSUSPEND,
          RESULT_TYPES.SUSPEND,
          RESULT_TYPES.COMMENT,
          RESULT_TYPES.RATE,
          RESULT_TYPES.DELETE,
          RESULT_TYPES.ARCHIVE,
          RESULT_TYPES.RESTORE
        ]?.includes(saveResult?.type)
      ) {
        setProp("saveResult", null)
      }
    }
  }, [saveResult])

  useEffect(() => {
    generateDocumentTitle("Subcontractors")
    getFilterOptions()
  }, [])

  useEffect(() => {
    void handleGetSubcontractors()
  }, [handleGetSubcontractors])

  return (
    <div className={"grid size-full"}>
      <Flex vertical gap={"middle"} className={"overflow-auto"}>
        <Flex justify={"space-between"} align={"center"}>
          <Flex vertical>
            <Title className={"text-start"} level={2}>
              {t("subcontractors.title")}
            </Title>
            <Text type={"secondary"}>{t("subcontractors.subtitle")}</Text>
          </Flex>
          <PermissionGate mode={"some"} paths={CREATE_PERMISSIONS} hideOnDenied>
            <Button
              type={"primary"}
              icon={<PlusOutlined />}
              onClick={openCreate}
            >
              {t("subcontractors.addButton")}
            </Button>
          </PermissionGate>
        </Flex>

        {rateId !== null && (
          <SubcontractorRateModal
            onSubmit={handleRateSubcontractor}
            visible
            onCancel={clearRateId}
          />
        )}

        {showSubcontractorForm && !subcontractorDetailsFetching && (
          <SubcontractorFormModal
            loading={subcontractorDetailsFetching}
            onSave={handleCreateSubcontractor}
            onCancel={handleSubcontractorFormCancel}
            initialValues={
              subcontractorDetails
                ? {
                    ...subcontractorDetails,
                    price_per_hour: subcontractorDetails?.price_per_hour
                      ? +subcontractorDetails.price_per_hour
                      : null
                  }
                : null
            }
          />
        )}

        <Suspense fallback={<Skeleton.Node active className={"size-full"} />}>
          {isMobile ? (
            <SubcontractorsListMobile
              subcontractors={subcontractors}
              page={page}
              count={count}
              pageSize={filters?.page_size ?? SCROLL_PAGE_SIZE}
              filters={filters}
              options={options}
              loading={fetching || saving}
              //
              viewAllowed={viewAllowed}
              editAllowed={editAllowed}
              deleteAllowed={deleteAllowed}
              inviteAllowed={inviteAllowed}
              //
              clearFilters={clearFilters}
              onPageChange={handleChangePage}
              onFilterChange={handleChangeFilter}
              onSelectRow={handleSelectRow}
            />
          ) : (
            <SubcontractorsTable
              subcontractors={subcontractors}
              next={data?.next}
              retryNext={data?.retryNext}
              loadFailed={data?.loadFailed}
              onRetry={handleGetSubcontractors}
              // Keeps the search and the All / Needs attention / Archived tab;
              // `clearFilters` resets those too.
              onClearFilters={() =>
                handleChangeFilter(clearedSubcontractorFilters(filters))
              }
              count={count}
              ordering={ordering}
              filters={filters}
              options={options}
              loading={fetching || saving}
              //
              viewAllowed={viewAllowed}
              editAllowed={editAllowed}
              deleteAllowed={deleteAllowed}
              inviteAllowed={inviteAllowed}
              //
              onLoadMore={handleGetMoreSubcontractors}
              onOrderingChange={setOrdering}
              onFilterChange={handleChangeFilter}
              onSelectRow={handleSelectRow}
            />
          )}
        </Suspense>
      </Flex>
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  data: state.subcontractors.result,
  fetching: state.subcontractors.fetching,
  saving: state.subcontractors.saveFetching,
  isMobile: state.nav.isMobile,
  filters: state.subcontractors.filters,
  options: state.subcontractors.filterOptions,
  permissions: state.auth.permissions.codes?.subcontractors,

  subcontractorDetailsFetching:
    state.subcontractors.subcontractorDetailsFetching,
  subcontractorDetails: state.subcontractors.subcontractorDetails,

  //   @todo remove it
  saveResult: state.subcontractors.saveResult
})

const mapDispatchToProps = {
  getFilterOptions: fetchSubcontractorsFilterOptions,
  getSubcontractors: fetchSubcontractors,
  getSubcontractorDetails: fetchSubcontractorDetails,
  setProp: subcontractorsSetProp,
  rateSubcontractor,
  setFilters: subcontractorsSetFilters,
  clearFilters: subcontractorsClearFilters,
  updateSubcontractor,
  createSubcontractor,
  createInviteSubcontractor,
  archiveSubcontractor,
  restoreSubcontractor,
  suspendSubcontractor,
  activateSubcontractor,
  inviteNoAccessSubcontractor,
  resendInviteSubcontractor
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(SubcontractorsPage)
